// ==================== FILTER TYPES ====================

export interface IRegion {
    code: string;
    name_ru: string;
    name_kz: string;
    text: string;
}

export interface IAbp {
    abp: number;
    name_ru: string;
    name_kz: string;
    text: string;
}

export interface IGu {
    id: number;
    code: string;
    name_ru: string;
    name_kk: string;
    text: string;
    budget_type: string;
}

export interface IPrg {
    prg: number;
    name_ru: string;
    name_kz: string;
    text: string;
}

export interface IPpr {
    ppr: number;
    name_ru: string;
    name_kz: string;
    text: string;
}

export interface ISpf {
    spf: number;
    name_ru: string;
    name_kz: string;
    text: string;
}

export interface IDevelop {
    text: string;
    code: number;
}

// ==================== API RESPONSE TYPES ====================

export interface IContractResponse {
    id: number;
    dt_reg: string;
    nom_za: string;
    nom_dog: string;
    rnn_supplier: string;
    supplier: string;
    item_description: string;
    summa_dog: number;
    invnum: string;
    pay_date: string;
    pay_amount: number;
    kod_tpk: string;
    gu: string;
    gu_bin: string;
    abp: number;
    prg: number;
    ppr: number;
    func: string;
    espk: string;
    source: "edit" | "iisk";
    obligation_sum: number;
    payment_sum: number;
    apps_count: number;
    gu_name?: string;
}

export interface IContractAppResponse {
    nom_za: string;
    dt_reg: string;
    summa_dog: number;
    pay_amount: number;
}

export interface IContractAppDetailResponse {
    id: number;
    source: "edit" | "iisk";
    nom_dog: string;
    dt_dog: string;
    dt_reg: string;
    rnn_supplier: string;
    supplier: string;
    item_description: string;
    spf: string;
    pay_date: string;
    summa_dog: number;
    pay_amount: number;
    nom_za: string;
    invnum: string;
    gu: string;
    abp: number;
    gu_bin: string;
    prg: number;
    ppr: number;
    kod_tpk: string;
    nom_uved: string;
    invnum_locked?: boolean;
    has_project?: boolean;
    expense_code?: string | null;
    bip_code?: string | null;
    stage?: string | null;
}

// ==================== PAGINATION ====================

export interface ICursor {
    cursor_dt: string;
    cursor_nom_dog: string;
    cursor_id: number;
}

export interface IContractListResponse {
    result: "success" | "error";
    data: IContractResponse[];
    next_cursor: ICursor | null;
    has_next: boolean;
    message?: string;
}

export interface IContractAppsResponse {
    data: IContractAppResponse[];
    count: number;
}

export interface IContractAppDetailListResponse {
    data: IContractAppDetailResponse[];
    count: number;
}

// ==================== FORM MODEL ====================

export interface IContractAppForm {
    id: number;
    budgetRegion: string; // kod_tpk
    abp: string;
    gu: string;
    gu_bin: string;
    prg: string;
    ppr: string;
    spf: string;
    numApp: string; // nom_za
    numContract: string; // nom_dog
    dateContract: Date; // dt_dog
    numNotify: string; // nom_uved
    binSupplier: string; // rnn_supplier
    supplier: string;
    dateReg: Date; // dt_reg
    description: string; // item_description
    payAmount: number; // pay_amount
    payDate: Date; // pay_date
    obligationAmount: number; // summa_dog
    invnum: string;
    source: "edit" | "iisk";
    hasProject: boolean; 
    projectCode: string;
    boundRegYear: number | null; 
}

export abstract class BaseForm409 implements IContractAppForm {
    public id = 0;
    public budgetRegion = "";
    public abp = "";
    public gu = "";
    public gu_bin = "";
    public prg = "";
    public ppr = "";
    public spf = "";
    public numApp = "";
    public numContract = "";
    public dateContract = new Date();
    public numNotify = "";
    public binSupplier = "";
    public supplier = "";
    public dateReg = new Date();
    public description = "";
    public payAmount = 0;
    public payDate = new Date();
    public obligationAmount = 0;
    public invnum = "";
    public source: "edit" | "iisk" = "edit";
    public hasProject = false;
    public projectCode = "";
    public boundRegYear: number | null = null;
}

export class AppDataClass extends BaseForm409 {}

// ==================== MAPPERS ====================

export function mapResponseToForm(
    data: IContractAppDetailResponse
): AppDataClass {
    const form = new AppDataClass();
    form.numApp = data.nom_za;
    form.numContract = data.nom_dog;
    form.dateContract = new Date(data.dt_dog);
    form.dateReg = new Date(data.dt_reg);
    form.binSupplier = data.rnn_supplier;
    form.supplier = data.supplier;
    form.description = data.item_description;
    form.obligationAmount = data.summa_dog;
    form.payAmount = data.pay_amount;
    form.payDate = new Date(data.pay_date);
    form.invnum = data.invnum;
    form.spf = data.spf;
    form.gu = data.gu;
    form.gu_bin = data.gu_bin;
    form.abp = String(data.abp);
    form.prg = String(data.prg);
    form.ppr = String(data.ppr);
    form.budgetRegion = data.kod_tpk;
    form.numNotify = data.nom_uved;
    form.source = data.source;
    form.hasProject = data.has_project === true;
    form.projectCode = [data.expense_code, data.bip_code, data.stage]
        .filter((v) => v)
        .join(" · ");
    form.boundRegYear = form.hasProject ? form.dateReg.getFullYear() : null;
    return form;
}

export function mapFormToRequest(form: AppDataClass): Record<string, any> {
    return {
        nom_za: form.numApp,
        nom_dog: form.numContract,
        dt_dog: form.dateContract.toISOString().split("T")[0],
        dt_reg: form.dateReg.toISOString().split("T")[0],
        num_notify: form.numNotify,
        rnn_supplier: form.binSupplier,
        supplier: form.supplier,
        item_description: form.description,
        summa_dog: form.obligationAmount,
        pay_amount: form.payAmount,
        pay_date: form.payDate.toISOString().split("T")[0],
        invnum: form.invnum,
        spf: form.spf,
        abp: form.abp,
        gu: form.gu,
        prg: form.prg,
        ppr: form.ppr,
        kod_tpk: form.budgetRegion,
    };
}

export interface IPaymentRow {
    id: number;
    dbId?: number;
    payDate: Date | null;
    payAmount: number | null;
    invoiceNum: string;
    source: "edit" | "iisk";
    formattedPayAmount: string;
    isReturnInvoice: boolean;
    invoiceNumSuffix: string;
    invnumLocked?: boolean;
}

// ==================== VALIDATION ====================

export interface ValidationErrors {
    [key: string]: string | undefined;
}

export function formatAmount(
    value: number | string | null | undefined
): string {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    const stringValue = String(value).replace(/\s/g, "").replace(/,/g, ".");

    const [intPartRaw, decimalPartRaw] = stringValue.split(".");

    const intPart = intPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

    if (decimalPartRaw !== undefined) {
        const decimal = decimalPartRaw.slice(0, 2).padEnd(2, "0");
        return `${intPart}.${decimal}`;
    }

    return intPart;
}

export type PaymentCompare = {
    id: number | null;
    payDate: number | null;
    payAmount: number;
    invoiceNum: string;
    source: string;
};